import MembersEmailPreferences from './MembersEmailPreferences.tsx';
import MembersPublicProfile from './MembersPublicProfile.tsx';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Copy, LogOut, Users } from 'lucide-react';
import { membersApi, membersError, Member, MembersDashboard, memberDate, memberStatus, money, contractLastDay, MemberDocument } from '../../services/membersApi.ts';
import { getSafeHttpsUrl } from '../../utils/safeUrl.ts';
import { Card, Loading, MembersShell, Notice, Pagination, primary } from './MembersShared.tsx';
function ProfileForm({partner}: {partner: Member; onSaved: () => void}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [documents, setDocuments] = useState<MemberDocument[]>([]);
  const locked = !!partner.provisioning_status && partner.provisioning_status !== 'not_started';
  async function loadDocuments() {
    setBusy(true); setError('');
    try {setDocuments((await membersApi.onboarding()).documents);}
    catch(err) {setError(membersError(err));} finally {setBusy(false);}
  }
  return <Card>
    <h2 className="text-lg font-semibold">Cadastro financeiro</h2>
    <p className="members-secondary my-3 text-sm">Complete ou revise seu cadastro em cinco etapas: acesso, titular, endereço, conta bancária e revisão. A abertura da conta de recebimento depende da análise cadastral do Asaas.</p>
    <p className="mb-5 text-sm">Situação: <strong>{memberStatus(partner.status)}</strong></p>
    <Link to="/members/register" className={primary}>{locked ? 'Consultar cadastro' : 'Retomar cadastro'}</Link>
    <div className="mt-6 space-y-4">
      <button disabled={busy} onClick={loadDocuments} className="members-input text-sm">Consultar documentos e aprovação</button>
      {error && <Notice error>{error}</Notice>}
      {documents.map(item => {
        const url = getSafeHttpsUrl(item.onboarding_url);
        return <article key={item.id} className="rounded-xl border border-[var(--members-border)] p-4 text-sm"><p>{item.description || 'Documento do cadastro'}</p>{url && <a href={url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block underline">Continuar cadastro no Asaas</a>}{item.manual_action_required && <p className="mt-2">{item.guidance}</p>}</article>;
      })}
    </div>
  </Card>;
}
export default function MembersPortal() {
  const navigate = useNavigate();
  const [data, setData] = useState<MembersDashboard | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);
  const [tab, setTab] = useState<'clients' | 'commissions' | 'profile' | 'public-profile' | 'email-preferences'>('clients');
  const [offset, setOffset] = useState(0);
  const [copied, setCopied] = useState(false);
  const refresh = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      setData(await membersApi.dashboard(offset));
    } catch (err) {
      if ((err as {
        response?: {
          status?: number;
        };
      })?.response?.status === 401) navigate('/members/login', {
        replace: true
      });else setError(membersError(err));
    } finally {
      setBusy(false);
    }
  }, [navigate, offset]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  async function logout() {
    if (!data) return;
    setBusy(true);
    try {
      await membersApi.logout(data.partner.csrf_token);
      navigate('/members/login', {
        replace: true
      });
    } catch (err) {
      setError(membersError(err));
    } finally {
      setBusy(false);
    }
  }
  const referralUrl = data ? `${window.location.origin}/members/indicar/${encodeURIComponent(data.partner.referral_code)}` : '';
  async function copy() {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
    } catch {
      setError('Não foi possível copiar. Selecione o link e copie manualmente.');
    }
  }
  const partnerNavigation = <nav className="flex flex-wrap gap-2" aria-label="Área do parceiro">{[['clients', 'Clínicas indicadas'], ['commissions', 'Mensalidades e comissões'], ['profile', 'Meu cadastro'], ['public-profile', 'Meu perfil público'], ['email-preferences', 'Preferências de e-mail']].map(([key, label]) => <button key={key} aria-current={tab === key ? 'page' : undefined} onClick={() => {
            setTab(key as typeof tab);
            setOffset(0);
          }} className={`rounded-xl px-4 py-3 text-sm ${tab === key ? 'bg-sminy-primary text-white font-semibold' : 'border border-gray-400/40'}`}>{label}</button>)}</nav>;
  return <MembersShell mobileNavigation={data ? partnerNavigation : undefined} action={<button disabled={busy} onClick={logout} className="flex items-center gap-2 rounded-xl border border-gray-400/40 px-4 py-2 text-sm disabled:opacity-50">
    <LogOut size={16} />Sair</button>}>
    <div className="space-y-6">
      <div>
        <p className="text-sm text-sminy-primary">MINHA PARCERIA</p>
        <h1 className="mt-2 text-2xl font-semibold">{data ? `Olá, ${data.partner.name}` : 'Sua área de parceiro'}</h1>
        <p className="mt-2 text-sm opacity-75">Acompanhe as clínicas que você indicou e os recebimentos de cada contrato, seja como agência, profissional de marketing ou cliente Sminy.</p>
      </div>{error && <Notice error>{error}
        <button className="underline" onClick={() => void refresh()}>Tentar novamente</button>
      </Notice>}{busy && <Loading />}{data && !busy && <>{data.partner.provisioning_status === 'not_started' && <Notice>Confira seu cadastro de recebimento e acompanhe o andamento. <Link className="underline" to="/members/register">Ver cadastro</Link></Notice>}<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[['Clínicas', data.summary.clients], ['Comissões previstas', money(data.summary.total_expected)], ['Aguardando repasse', money(data.summary.total_pending)], ['Repassadas', money(data.summary.total_paid)]].map(([label, value]) => <Card key={label}>
            <p className="text-sm opacity-75">{label}</p>
            <p className="mt-3 text-2xl font-semibold">{value}</p>
          </Card>)}</div><Card>
          <h2 className="font-semibold">Seu link de indicação</h2>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row">
            <input aria-label="Seu link de indicação" className="min-w-0 flex-1 rounded-xl border border-gray-400/40 bg-transparent p-3 text-sm" readOnly value={referralUrl} onFocus={event => event.currentTarget.select()} />
            <button onClick={copy} className={primary}>
              <Copy size={16} />{copied ? 'Copiado' : 'Copiar link'}</button>
          </div>
        </Card><div className="hidden md:block">{partnerNavigation}</div>{tab === 'email-preferences' ? <MembersEmailPreferences csrfToken={data.partner.csrf_token} /> : tab === 'public-profile' ? <MembersPublicProfile csrfToken={data.partner.csrf_token} /> : tab === 'profile' ? <ProfileForm partner={data.partner} onSaved={() => void refresh()} /> : <Card>{tab === 'clients' ? <><h2 className="font-semibold">Clínicas indicadas</h2>{data.referrals.length === 0 ? <div className="py-12 text-center">
              <Users className="mx-auto mb-3 text-sminy-primary" />
              <p>Nenhuma indicação por aqui ainda.</p>
              <p className="mt-2 text-sm opacity-75">Compartilhe seu link com clínicas da sua rede para começar. Você não precisa publicar um perfil no catálogo.</p>
            </div> : <div className="members-table-frame mt-4"><table className="members-table members-clients-table"><caption className="sr-only">Clínicas indicadas</caption><thead><tr><th scope="col">Clínica</th><th scope="col">Datas</th><th scope="col">Situação</th></tr></thead><tbody>{data.referrals.map(item => <tr key={item.id}><th scope="row" className="font-medium">{item.clinic_name}</th><td><span className="block">Indicada em {memberDate(item.created_at)}</span><span className="members-muted mt-2 block">Contrato: {item.contract_start ? <>{memberDate(item.contract_start)} a {contractLastDay(item.contract_end)}</> : 'Ainda não iniciado'}</span></td><td>{memberStatus(item.status)}</td></tr>)}</tbody></table></div>}</> : <><h2 className="font-semibold">Mensalidades e comissões</h2><p className="my-3 text-sm opacity-75">O pagamento da clínica e o repasse da sua comissão são acompanhados separadamente. Estornos: {money(data.summary.total_refunded)}.</p>{data.commissions.length === 0 ? <p className="py-10 text-center text-sm">As comissões aparecerão aqui quando houver mensalidades vinculadas.</p> : <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] text-left text-sm">
                <thead>
                  <tr>{['Clínica', 'Vencimento', 'Mensalidade', 'Comissão', 'Repasse'].map(label => <th key={label} className="py-3 pr-4 font-medium">{label}</th>)}</tr>
                </thead>
                <tbody>{data.commissions.map(item => <tr key={item.id} className="border-t border-gray-400/20">
                    <td className="py-4 pr-4">{item.clinic_name}</td>
                    <td className="pr-4">{memberDate(item.due_date)}</td>
                    <td className="pr-4">{memberStatus(item.payment_status)}</td>
                    <td className="pr-4">{money(item.amount)}</td>
                    <td>{memberStatus(item.status)}</td>
                  </tr>)}</tbody>
              </table>
            </div>}</>}
          <Pagination offset={offset} total={tab === 'clients' ? data.referrals_total : data.commissions_total} onChange={setOffset} />
        </Card>}</>}{!data && !busy && !error && <Link to="/members/login">Entrar na minha conta</Link>}</div>
  </MembersShell>;
}
